<template>
    <div>
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <button type="button" @click="$router.go(-1)" class="btn-back">
            <font-awesome-icon icon="arrow-left" />
          </button>
          <h5 class="page-title mt-2">
            Record Payment
            <span v-if="invoice?.invoice_no" class="batch-pill ms-2">#{{ invoice.invoice_no }}</span>
          </h5>
          <p class="page-sub" v-if="invoice?.customer?.customer_name">{{ invoice.customer.customer_name }}</p>
        </div>
        <div class="page-actions" v-if="!loading && invoice">
          <button class="btn-outline" @click="downloadPDF">
            <font-awesome-icon icon="file-pdf" class="me-2 text-danger" /> Download PDF
          </button>
        </div>
      </div>
  
      <!-- Loading -->
      <div v-if="loading" class="page-loading">
        <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
        <p class="mt-3">Loading invoice...</p>
      </div>
  
      <div v-else class="page-layout">
  
        <!-- ── Left: Record Payment Form ── -->
        <div class="form-col">
  
          <!-- Invoice Already Paid -->
          <div v-if="invoice?.status == 2" class="paid-banner">
            <div class="paid-icon"><font-awesome-icon icon="check-circle" /></div>
            <div>
              <p class="paid-title">Fully Paid</p>
              <p class="paid-sub">This invoice has been fully settled.</p>
            </div>
          </div>
  
          <div v-else class="form-card">
            <div class="form-card-header">
              <div class="form-card-icon"><font-awesome-icon icon="pen" /></div>
              <div>
                <p class="form-card-title">Record Payment</p>
                <p class="form-card-sub">Manually record a payment for this invoice</p>
              </div>
            </div>
  
            <!-- Balance Due -->
            <div class="amount-due-chip">
              <span class="amount-due-label">Balance Due</span>
              <span class="amount-due-value">{{ invoice?.currency?.symbol }}{{ formatPrice(invoice?.balance_amount) }}</span>
            </div>
  
            <div class="form-fields">
  
              <div class="field-group">
                <label class="field-label">Payment Type</label>
                <select class="field-input field-select" v-model="payment.payment_type_id" :disabled="permission.record_invoice_payment != 1">
                  <option disabled value="">— Select payment type —</option>
                  <option v-for="(t, i) in paymentTypes" :key="i" :value="t.id">{{ t.name }}</option>
                </select>
              </div>
  
              <div class="field-group">
                <label class="field-label">Payment Date</label>
                <div class="datepicker-wrap">
                  <font-awesome-icon icon="calendar" class="datepicker-icon" />
                  <Datepicker v-model="payment.payment_date" :enableTimePicker="false" class="clean-datepicker" />
                </div>
              </div>
  
              <div class="field-group">
                <label class="field-label">Payment Amount</label>
                <div class="amount-input-wrap">
                  <span class="amount-currency">{{ invoice?.currency?.symbol }}</span>
                  <input
                    type="text"
                    class="field-input amount-input"
                    v-model="payment.amount"
                    placeholder="0.00"
                    onkeypress="return /^[0-9.]+$/i.test(event.key)"
                    @keyup="handleAmount"
                    :disabled="permission.record_invoice_payment != 1"
                  />
                </div>
              </div>
  
              <div class="field-group">
                <label class="field-label">Remaining Balance</label>
                <div class="balance-display" :class="payment.balance > 0 ? 'balance-display--outstanding' : 'balance-display--clear'">
                  <font-awesome-icon :icon="payment.balance > 0 ? 'exclamation-circle' : 'check-circle'" class="me-2" />
                  {{ invoice?.currency?.symbol }}{{ formatPrice(payment.balance) }}
                </div>
              </div>
  
            </div>
  
            <button class="submit-btn" :disabled="crudLoading || permission.record_invoice_payment != 1" @click.prevent="makePayment">
              <span v-if="crudLoading" class="spinner-border spinner-border-sm me-2" role="status"></span>
              <font-awesome-icon v-else icon="check-circle" class="me-2" />
              {{ crudLoading ? 'Processing...' : 'Record Payment' }}
            </button>
  
            <p v-if="permission.record_invoice_payment != 1" class="permission-hint">
              <font-awesome-icon icon="lock" class="me-1" /> You don't have permission to record payments.
            </p>
  
          </div>
        </div>
  
        <!-- ── Right: Invoice Preview ── -->
        <div class="invoice-col" id="printMe">
  
          <!-- Invoice Header -->
          <div class="inv-header">
            <div>
              <p class="inv-label">INVOICE</p>
              <p class="inv-number">#{{ invoice?.invoice_no }}</p>
            </div>
            <img v-if="loggedInUser?.business?.company_image" :src="host + loggedInUser?.business?.company_image" alt="Logo" class="inv-logo" />
          </div>
  
          <!-- Parties -->
          <div class="inv-parties">
            <div>
              <p class="party-label">Bill To</p>
              <p class="party-name">{{ invoice?.customer?.customer_name }}</p>
              <p class="party-detail" v-if="invoice?.customer?.address">{{ invoice.customer.address }}</p>
              <p class="party-detail" v-if="invoice?.customer?.email">{{ invoice.customer.email }}</p>
            </div>
            <div>
              <p class="party-label">From</p>
              <p class="party-name">{{ loggedInUser?.business?.company_name }}</p>
              <p class="party-detail" v-if="loggedInUser?.business?.address">{{ loggedInUser.business.address }}</p>
              <p class="party-detail" v-if="loggedInUser?.business?.company_email">{{ loggedInUser.business.company_email }}</p>
              <p class="party-detail" v-if="loggedInUser?.business?.phone">{{ loggedInUser.business.phone }}</p>
            </div>
          </div>
  
          <!-- Meta -->
          <div class="inv-meta">
            <div class="inv-meta-item"><span class="inv-meta-key">Invoice Date</span><span class="inv-meta-val">{{ dateTime(invoice?.invoice_date) }}</span></div>
            <div class="inv-meta-item"><span class="inv-meta-key">Due Date</span><span class="inv-meta-val">{{ dateTime(invoice?.due_date) }}</span></div>
            <div class="inv-meta-item"><span class="inv-meta-key">Currency</span><span class="inv-meta-val">{{ invoice?.currency?.name }} ({{ invoice?.currency?.symbol }})</span></div>
          </div>
  
          <!-- Line Items -->
          <div class="inv-items-wrap">
            <table class="inv-table">
              <thead>
                <tr>
                  <th style="width:40%">Description</th>
                  <th class="tc">Qty</th>
                  <th class="tr">Price</th>
                  <th class="tr">Tax</th>
                  <th class="tr">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, i) in invoice?.items" :key="i">
                  <td>{{ item?.description }}</td>
                  <td class="tc text-muted-cell">{{ item?.quantity }}</td>
                  <td class="tr">{{ $filters.formatMoney(item?.price) }}</td>
                  <td class="tr text-muted-cell">
                    <span v-if="item?.apply_tax">{{ $filters.formatMoney(item?.tax_amount) }}</span>
                    <span v-else class="na-pill">N/A</span>
                  </td>
                  <td class="tr fw-bold">{{ $filters.formatMoney(item?.total) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
  
          <!-- Totals -->
          <div class="inv-totals">
            <div class="inv-total-row"><span>Subtotal</span><span>{{ $filters.formatMoney(invoice?.sub_total_amount, invoice?.currency?.symbol) }}</span></div>
            <div class="inv-total-row" v-if="invoice?.discount_amount > 0"><span>Discount</span><span class="deduction-value">− {{ $filters.formatMoney(invoice?.discount_amount, invoice?.currency?.symbol) }}</span></div>
            <div class="inv-total-row" v-if="invoice?.tax_amount > 0"><span>Tax</span><span>{{ $filters.formatMoney(invoice?.tax_amount, invoice?.currency?.symbol) }}</span></div>
            <div class="inv-total-divider"></div>
            <div class="inv-total-row inv-total-row--grand"><span>Total</span><span>{{ $filters.formatMoney(invoice?.total_amount, invoice?.currency?.symbol) }}</span></div>
          </div>
  
          <!-- Payment Summary -->
          <div class="inv-payment-summary">
            <p class="inv-section-title"><font-awesome-icon icon="money-bill-wave" class="me-2" />Payment Summary</p>
            <div class="inv-total-row"><span>Amount Paid</span><span class="earning-value fw-bold">{{ $filters.formatMoney(invoice?.paid_amount, invoice?.currency?.symbol) }}</span></div>
            <div class="inv-total-row" :class="invoice?.balance_amount > 0 ? 'inv-balance--outstanding' : ''">
              <span>Balance Due</span>
              <span class="fw-bold" :class="invoice?.balance_amount > 0 ? 'deduction-value' : 'earning-value'">
                {{ $filters.formatMoney(invoice?.balance_amount ?? 0, invoice?.currency?.symbol) }}
              </span>
            </div>
          </div>
  
          <div class="inv-doc-footer">Generated by Mapiibs Solution</div>
  
        </div>
      </div>
  
    </div>
  </template>
  
  <script>
  import { useToast }   from 'vue-toastification';
  import axios          from 'axios';
  import { reusableMixin } from '../../../lib/reusableMixin';
  import { useStore }   from 'vuex';
  import { useRouter }  from 'vue-router';
  import host           from '../../../main';
  import jsPDF          from 'jspdf';
  import html2canvas    from 'html2canvas';
  
  export default {
    mixins: [reusableMixin],
  
    data() {
      return {
        invoice:      null,
        paymentTypes: [],
        loading:      true,
        crudLoading:  false,
        toast:        useToast(),
        router:       useRouter(),
        store:        useStore(),
  
        payment: {
          amount:          0,
          balance:         0,
          payment_type_id: '',
          payment_date:    new Date(),
          invoice_id:      '',
        },
      };
    },
  
    computed: {
      loggedInUser() { return this.store.state.getLoggedInUser; },
      host()         { return host.hostName; },
      permission()   { return this.store.state.getCurrentUserPermission; },
    },
  
    created() {
      Promise.all([ this.fetchData(), this.fetchPaymentTypes() ]);
      this.getCompany();
    },
  
    methods: {
      async fetchData() {
        this.loading = true;
        return axios.get(`/invoice/detail/${this.$route.params.id}`)
          .then(response => {
            this.invoice             = response.data.data;
            this.payment.balance     = this.invoice.balance_amount;
            this.payment.amount      = this.invoice.balance_amount;
            this.payment.invoice_id  = this.invoice.id;
            this.loading             = false;
          })
          .catch(() => { this.loading = false; });
      },
  
      async fetchPaymentTypes() {
        return axios.get('/settings/payment_type')
          .then(response => { this.paymentTypes = response.data.data ?? []; })
          .catch(() => {});
      },
  
      handleAmount() {
        const bal = this.invoice?.balance_amount;
        let rem   = (bal - this.payment.amount).toFixed(2);
        if (rem < 0) { this.payment.amount = bal; rem = 0; }
        this.payment.balance = rem;
      },
  
      makePayment() {
        if (!this.payment.payment_type_id) { this.toast.error('Select a payment type'); return; }
        if (!this.payment.payment_date)    { this.toast.error('Select a payment date'); return; }
        if (!this.payment.amount || this.payment.amount < 1) { this.toast.error('Enter a valid payment amount'); return; }
        this.crudLoading = true;
        axios.post('/invoice/record-payment', this.payment)
          .then(response => {
            this.crudLoading = false;
            this.toast.success(response.data.message);
            this.$router.push({ name: 'Invoices' });
          })
          .catch(error => {
            this.crudLoading = false;
            this.toast.error(error.response?.data?.message?.[0] ?? 'Something went wrong');
          });
      },
  
      async downloadPDF() {
        const el     = document.getElementById('printMe');
        const canvas = await html2canvas(el, { scale:2, useCORS:true });
        const pdf    = new jsPDF('p', 'mm', 'a4');
        const w      = pdf.internal.pageSize.getWidth();
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, w, (canvas.height * w) / canvas.width);
        pdf.save(`Invoice_${this.invoice?.invoice_no}.pdf`);
      },
  
      formatPrice(value) {
        if (!value && value !== 0) return '0.00';
        return Number(value).toLocaleString(undefined, { minimumFractionDigits:2, maximumFractionDigits:2 });
      },
    },
  };
  </script>
  
  <style scoped>
  /* ── Page Header ── */
  .page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
  .page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; display:flex; align-items:center; flex-wrap:wrap; gap:8px; }
  .page-sub     { font-size:13px; color:#7a90b0; margin:0; }
  .page-actions { display:flex; align-items:center; gap:8px; }
  .btn-back     { display:inline-flex; align-items:center; gap:7px; background:transparent; border:none; color:#7a90b0; font-size:13px; cursor:pointer; padding:0; margin-bottom:4px; }
  .btn-back:hover { color:#1a2b45; }
  .batch-pill   { display:inline-block; background:#edf4ff; color:#004AAD; border:1px solid #c5d6f5; border-radius:20px; padding:2px 12px; font-size:13px; font-weight:600; }
  .btn-outline  { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:8px 14px; border-radius:8px; cursor:pointer; transition:background .15s; }
  .btn-outline:hover { background:#f4f7fb; }
  .ms-2 { margin-left:8px; } .me-2 { margin-right:7px; } .mt-2 { margin-top:8px; } .mt-3 { margin-top:12px; }
  
  /* ── Loading ── */
  .page-loading { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:80px 24px; color:#7a90b0; }
  
  /* ── Layout ── */
  .page-layout { display:grid; grid-template-columns:320px 1fr; gap:24px; align-items:start; }
  
  /* ── Payment Form ── */
  .form-col { display:flex; flex-direction:column; gap:12px; }
  .form-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:22px; }
  .form-card-header { display:flex; align-items:center; gap:12px; margin-bottom:16px; }
  .form-card-icon   { width:40px; height:40px; border-radius:10px; background:rgba(0,74,173,.1); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:16px; flex-shrink:0; }
  .form-card-title  { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
  .form-card-sub    { font-size:12px; color:#7a90b0; margin:0; }
  
  .amount-due-chip { display:flex; justify-content:space-between; align-items:center; background:rgba(0,74,173,.06); border:1px solid #c5d6f5; border-radius:10px; padding:12px 14px; margin-bottom:18px; }
  .amount-due-label { font-size:12px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#004AAD; }
  .amount-due-value { font-size:20px; font-weight:800; color:#004AAD; }
  
  .form-fields { display:flex; flex-direction:column; gap:13px; margin-bottom:18px; }
  .field-group  { display:flex; flex-direction:column; gap:5px; }
  .field-label  { font-size:13px; font-weight:600; color:#1a2b45; }
  .field-input  { padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13.5px; color:#1a2b45; background:#fff; outline:none; width:100%; transition:border-color .15s; font-family:inherit; }
  .field-input:focus { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
  .field-input:disabled { background:#f8fafc; color:#7a90b0; cursor:not-allowed; }
  .field-select { appearance:auto; cursor:pointer; }
  
  .datepicker-wrap { position:relative; }
  .datepicker-icon { position:absolute; left:11px; top:50%; transform:translateY(-50%); color:#7a90b0; font-size:13px; z-index:2; pointer-events:none; }
  :deep(.clean-datepicker) { border:1px solid #dde5f0 !important; border-radius:8px !important; background:#fff !important; padding-left:32px !important; }
  :deep(.clean-datepicker input) { font-size:13.5px; color:#1a2b45; }
  
  .amount-input-wrap { display:flex; align-items:center; border:1px solid #dde5f0; border-radius:8px; overflow:hidden; }
  .amount-currency   { padding:9px 12px; background:#f4f7fb; color:#7a90b0; font-weight:700; font-size:13.5px; border-right:1px solid #dde5f0; flex-shrink:0; }
  .amount-input      { border:none !important; border-radius:0 !important; box-shadow:none !important; }
  
  .balance-display { display:flex; align-items:center; border-radius:8px; padding:10px 14px; font-size:14px; font-weight:700; }
  .balance-display--outstanding { background:#fff0f0; color:#e03535; }
  .balance-display--clear       { background:#f0fdf4; color:#16a34a; }
  
  .submit-btn { width:100%; padding:12px; border-radius:8px; border:none; background:#004AAD; color:#fff; font-size:14px; font-weight:600; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:background .15s; }
  .submit-btn:hover:not(:disabled) { background:#003b8e; }
  .submit-btn:disabled { opacity:.6; cursor:not-allowed; }
  
  .permission-hint { font-size:12.5px; color:#7a90b0; text-align:center; margin-top:10px; }
  
  /* Paid banner */
  .paid-banner { display:flex; align-items:center; gap:14px; background:#f0fdf4; border:1px solid #bbf7d0; border-radius:12px; padding:20px; }
  .paid-icon   { width:48px; height:48px; border-radius:50%; background:rgba(22,163,74,.15); color:#16a34a; display:flex; align-items:center; justify-content:center; font-size:22px; flex-shrink:0; }
  .paid-title  { font-size:15px; font-weight:700; color:#16a34a; margin:0 0 4px; }
  .paid-sub    { font-size:13px; color:#4a5e78; margin:0; }
  
  /* ── Invoice Preview ── */
  .invoice-col { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
  
  .inv-header { display:flex; justify-content:space-between; align-items:center; padding:28px 28px 20px; background:linear-gradient(135deg,#051B35,#004AAD); }
  .inv-label  { font-size:11px; text-transform:uppercase; letter-spacing:.1em; color:rgba(255,255,255,.6); margin:0 0 4px; }
  .inv-number { font-size:26px; font-weight:900; color:#fff; margin:0; }
  .inv-logo   { max-width:80px; max-height:50px; object-fit:contain; border-radius:6px; background:#fff; padding:4px; }
  
  .inv-parties { display:grid; grid-template-columns:1fr 1fr; gap:20px; padding:20px 28px; border-bottom:1px solid #e4eaf2; }
  .party-label { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:#7a90b0; margin:0 0 6px; }
  .party-name  { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 3px; text-transform:uppercase; }
  .party-detail { font-size:12.5px; color:#4a5e78; margin:0 0 2px; }
  
  .inv-meta { display:flex; border-bottom:1px solid #e4eaf2; }
  .inv-meta-item { flex:1; padding:12px 16px; border-right:1px solid #e4eaf2; }
  .inv-meta-item:last-child { border-right:none; }
  .inv-meta-key { display:block; font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; margin-bottom:4px; }
  .inv-meta-val { font-size:13px; font-weight:600; color:#1a2b45; }
  
  .inv-items-wrap { overflow-x:auto; }
  .inv-table { width:100%; border-collapse:collapse; min-width:440px; }
  .inv-table thead tr { background:#f8fafc; }
  .inv-table thead th { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:10px 16px; border-bottom:1px solid #e4eaf2; border-top:1px solid #e4eaf2; }
  .inv-table tbody tr { border-bottom:1px solid #f0f4f8; }
  .inv-table tbody tr:last-child { border-bottom:none; }
  .inv-table tbody td { padding:11px 16px; font-size:13px; }
  .tc { text-align:center; }
  .tr { text-align:right; }
  .fw-bold { font-weight:700; }
  .text-muted-cell { color:#4a5e78; }
  .na-pill { font-size:11px; color:#aab8cc; background:#f4f7fb; border-radius:4px; padding:1px 6px; }
  
  .inv-totals,.inv-payment-summary { padding:16px 28px; border-top:1px solid #e4eaf2; }
  .inv-total-row { display:flex; justify-content:space-between; align-items:center; font-size:13.5px; color:#4a5e78; margin-bottom:8px; }
  .inv-total-row:last-child { margin-bottom:0; }
  .inv-total-divider { height:1px; background:#e4eaf2; margin:8px 0; }
  .inv-total-row--grand { font-size:17px; font-weight:900; color:#1a2b45; }
  .inv-total-row--grand span:last-child { color:#004AAD; }
  .inv-balance--outstanding { background:rgba(224,53,53,.04); border-radius:8px; padding:6px 8px; margin-left:-8px; margin-right:-8px; }
  .deduction-value { color:#e03535; }
  .earning-value   { color:#16a34a; }
  .inv-section-title { font-size:13px; font-weight:700; color:#1a2b45; margin:0 0 12px; display:flex; align-items:center; }
  .inv-doc-footer { text-align:center; padding:14px; background:#f8fafc; border-top:1px solid #e4eaf2; font-size:12px; color:#aab8cc; }
  
  /* ── Responsive ── */
  @media(max-width:991px) { .page-layout { grid-template-columns:1fr; } }
  @media(max-width:767px) { .inv-parties { grid-template-columns:1fr; } .inv-meta { flex-direction:column; } .inv-meta-item { border-right:none; border-bottom:1px solid #e4eaf2; } .page-header { flex-direction:column; } }
  </style>